import React, { useState } from 'react';
import { naytiveAPI } from '../services/api';

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'IN', name: 'India' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SG', name: 'Singapore' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'TH', name: 'Thailand' },
];
const LEGAL_TYPES = {
  US: [['delaware_llc', 'Delaware LLC'], ['delaware_c_corp', 'Delaware C-Corp'], ['wyoming_llc', 'Wyoming LLC'], ['wyoming_c_corp', 'Wyoming C-Corp']],
  IN: [['india_pvt_ltd', 'India Pvt Ltd'], ['india_llp', 'India LLP']],
  AE: [['uae_freezone', 'UAE Free Zone'], ['uae_mainland', 'UAE Mainland']],
  SG: [['singapore_pte_ltd', 'Singapore Pte Ltd']],
  GB: [['uk_ltd', 'UK Ltd']],
  TH: [['thailand_co_ltd', 'Thailand Co Ltd']],
};
const DOC_TYPES = [
  ['certificate_of_incorporation', 'Certificate of Incorporation'],
  ['incorporation_doc', 'Incorporation document (other)'],
  ['ein_letter', 'EIN letter'],
  ['operating_agreement', 'Operating agreement'],
  ['good_standing', 'Certificate of good standing'],
  ['other', 'Other document'],
];

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] || '');
  r.onerror = reject;
  r.readAsDataURL(file);
});

const input = { padding: '9px 11px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, background: '#fff', width: '100%', boxSizing: 'border-box' };
const label = { fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 5, display: 'block' };
const btn = { padding: '10px 18px', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: '#013399', color: '#fff' };

export default function CreateCompany() {
  const [form, setForm] = useState({
    email: '', name: '', countryCode: 'US', legalType: 'delaware_llc',
    stateOrRegion: 'Delaware', incorporationDate: '', entityNumber: '',
  });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null); // { entityId, userId, workspaceId }
  const [error, setError] = useState(null);

  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState('certificate_of_incorporation');
  const [uploading, setUploading] = useState(false);
  const [docs, setDocs] = useState([]); // uploaded results with extracted fields
  const [docError, setDocError] = useState(null);

  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === 'countryCode') next.legalType = (LEGAL_TYPES[v] || [['other', 'Other']])[0][0];
    return next;
  });

  const create = async () => {
    setError(null);
    if (!form.email.trim() || !form.name.trim()) { setError('Client email and company name are required.'); return; }
    setCreating(true);
    try {
      const r = await naytiveAPI.createEntity({
        email: form.email.trim(), name: form.name.trim(), countryCode: form.countryCode,
        legalType: form.legalType, stateOrRegion: form.stateOrRegion || null,
        incorporationDate: form.incorporationDate || null, entityNumber: form.entityNumber || null,
      });
      if (r.data && r.data.entityId) setCreated(r.data);
      else setError((r.data && (r.data.error || r.data.message)) || 'Failed to create the company.');
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || 'Failed to create the company.');
    } finally { setCreating(false); }
  };

  const upload = async () => {
    setDocError(null);
    if (!file) { setDocError('Pick a file first.'); return; }
    setUploading(true);
    try {
      const fileBase64 = await fileToBase64(file);
      const r = await naytiveAPI.uploadEntityDoc({
        userId: created.userId, entityId: created.entityId,
        fileName: file.name, fileMime: file.type || 'application/octet-stream',
        fileBase64, targetHint: docType,
      });
      if (r.data && r.data.ok) {
        setDocs((d) => [{ ...r.data, at: Date.now() }, ...d]);
        setFile(null);
      } else setDocError((r.data && (r.data.error || r.data.message)) || 'Upload failed.');
    } catch (e) {
      setDocError(e.response?.data?.error || e.response?.data?.message || 'Upload failed.');
    } finally { setUploading(false); }
  };

  return (
    <div style={{ padding: 24, maxWidth: 760, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px' }}>New Company</h1>
      <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 20px' }}>
        Create an incorporated company for a client and upload its documents — extraction runs automatically.
      </p>

      {/* Step 1 — company */}
      <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, padding: 20, background: '#fff', opacity: created ? 0.6 : 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>1 · Company details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ gridColumn: '1 / span 2' }}>
            <span style={label}>Client email *</span>
            <input style={input} placeholder="client@example.com" value={form.email} disabled={!!created}
              onChange={(e) => set('email', e.target.value)} />
          </div>
          <div style={{ gridColumn: '1 / span 2' }}>
            <span style={label}>Company name *</span>
            <input style={input} placeholder="House of Svolta, Desire Architecture LLC" value={form.name} disabled={!!created}
              onChange={(e) => set('name', e.target.value)} />
          </div>
          <div>
            <span style={label}>Jurisdiction</span>
            <select style={input} value={form.countryCode} disabled={!!created} onChange={(e) => set('countryCode', e.target.value)}>
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <span style={label}>Legal type</span>
            <select style={input} value={form.legalType} disabled={!!created} onChange={(e) => set('legalType', e.target.value)}>
              {(LEGAL_TYPES[form.countryCode] || [['other', 'Other']]).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <span style={label}>State / region</span>
            <input style={input} placeholder="Delaware" value={form.stateOrRegion} disabled={!!created}
              onChange={(e) => set('stateOrRegion', e.target.value)} />
          </div>
          <div>
            <span style={label}>Formation date</span>
            <input type="date" style={input} value={form.incorporationDate} disabled={!!created}
              onChange={(e) => set('incorporationDate', e.target.value)} />
          </div>
          <div style={{ gridColumn: '1 / span 2' }}>
            <span style={label}>Registration / file number</span>
            <input style={input} placeholder="10768794" value={form.entityNumber} disabled={!!created}
              onChange={(e) => set('entityNumber', e.target.value)} />
          </div>
        </div>
        {error && <div style={{ color: '#DC2626', fontSize: 13, marginTop: 12 }}>{error}</div>}
        {!created && (
          <button style={{ ...btn, marginTop: 16, opacity: creating ? 0.6 : 1 }} disabled={creating} onClick={create}>
            {creating ? 'Creating…' : 'Create company'}
          </button>
        )}
        {created && (
          <div style={{ color: '#15803D', fontSize: 13, marginTop: 14 }}>
            ✓ Company created (entity #{created.entityId}{created.duplicate ? ' — already existed' : ''}). Now upload its documents below.
          </div>
        )}
      </div>

      {/* Step 2 — documents */}
      {created && (
        <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, padding: 20, background: '#fff', marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>2 · Upload documents</div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px' }}>
              <span style={label}>Document type</span>
              <select style={input} value={docType} onChange={(e) => setDocType(e.target.value)}>
                {DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div style={{ flex: '2 1 260px' }}>
              <span style={label}>File (PDF / PNG / JPG)</span>
              <input type="file" accept=".pdf,image/png,image/jpeg" style={{ ...input, padding: 7 }}
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
            <button style={{ ...btn, opacity: uploading ? 0.6 : 1 }} disabled={uploading} onClick={upload}>
              {uploading ? 'Uploading + extracting…' : 'Upload'}
            </button>
          </div>
          {docError && <div style={{ color: '#DC2626', fontSize: 13, marginTop: 10 }}>{docError}</div>}

          {docs.map((d, i) => (
            <div key={d.at + '-' + i} style={{ border: '1px solid #E8ECF1', borderRadius: 10, padding: 14, marginTop: 14, background: '#FafBfC' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{d.fileName} <span style={{ color: '#9CA3AF', fontWeight: 400 }}>· {d.docTypeName}</span></div>
              {d.summary && <div style={{ fontSize: 12, color: '#4B5563', margin: '4px 0 8px' }}>{d.summary}</div>}
              <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>EXTRACTED FIELDS</div>
              {d.fields && Object.keys(d.fields).length ? (
                <table style={{ fontSize: 12, borderCollapse: 'collapse' }}>
                  <tbody>
                    {Object.entries(d.fields).map(([k, v]) => (
                      <tr key={k}>
                        <td style={{ padding: '2px 12px 2px 0', color: '#6B7280', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{k}</td>
                        <td style={{ padding: '2px 0', color: '#111827' }}>{v == null ? '—' : (typeof v === 'object' ? JSON.stringify(v) : String(v))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <div style={{ fontSize: 12, color: '#9CA3AF' }}>No fields extracted.</div>}
            </div>
          ))}

          <div style={{ marginTop: 18, fontSize: 12, color: '#6B7280' }}>
            Done? The company + its documents now show in the client's account. You can add more documents above, or{' '}
            <button onClick={() => { setCreated(null); setDocs([]); setForm({ email: '', name: '', countryCode: 'US', legalType: 'delaware_llc', stateOrRegion: 'Delaware', incorporationDate: '', entityNumber: '' }); }}
              style={{ background: 'none', border: 'none', color: '#013399', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              create another company
            </button>.
          </div>
        </div>
      )}
    </div>
  );
}
